/**
 * Builds an Atlas-shaped theme object where semantic colors, core branding colors,
 * and typography (semantic + primitives) use Vault values instead of Atlas values.
 *
 * The returned object is structurally identical to `createAtlasTheme('light')`.
 * Components using `theme.atlas.*` remain unchanged — only the resolved values differ.
 *
 * Pass-through (no Vault equivalent / structural):
 *   - core.color.green / yellow / blue / purple / indigo / flamingo (chart palettes,
 *     accessed via dataVisualization which is overridden separately)
 *   - core.spacing, core.radius, core.shadow
 *   - semantic.shadow, semantic.radius
 */

import { createAtlasTheme, type AtlasThemeResolved } from '../createAtlasTheme';
import { vaultColorOverrides } from './vaultColorOverrides';
import {
  vaultCoreBrand,
  vaultCoreGray,
  vaultCoreGreen,
  vaultCoreRed,
  vaultCoreYellow,
} from './vaultCoreColorOverrides';
import { vaultTypographyOverrides } from './vaultTypographyOverrides';
import { vaultTypographyPrimitiveOverrides } from './vaultTypographyPrimitiveOverrides';

/** Full Atlas theme (the reference for non-overridden / structural tokens) */
const atlasTheme = createAtlasTheme('light');

/**
 * Atlas-shaped theme with Vault color and typography values.
 * Use this when the ATLAS_THEME feature flag is OFF (or for OPR pages).
 *
 * Cast to AtlasThemeResolved so it satisfies DefaultTheme.atlas.
 * The runtime structure is identical — only leaf hex / font values differ.
 */
export const vaultVariantTheme = {
  ...atlasTheme,
  core: {
    ...atlasTheme.core,
    color: {
      ...atlasTheme.core.color,
      gray: vaultCoreGray,
      red: vaultCoreRed,
      green: vaultCoreGreen,
      yellow: vaultCoreYellow,
      brand: vaultCoreBrand,
    },
  },
  semantic: {
    ...atlasTheme.semantic,
    color: vaultColorOverrides,
  },
  typography: {
    ...atlasTheme.typography,
    ...vaultTypographyPrimitiveOverrides,
    semantic: vaultTypographyOverrides,
  },
} as unknown as AtlasThemeResolved;
